// src/components/CandidatePreview.jsx
import { useState } from "react";

const SENIORITY_OPTIONS = [
  "C-level", "Director", "Senior Manager", "Middle", "Junior", ""
];
const SCALE_OPTIONS = [
  "Startup micro", "Startup small", "Mid market", "Large corp", "Top100 RU", "International", ""
];
const TRAJECTORY_OPTIONS = [
  "Ascending", "Stable", "Declining", "Pivot", ""
];
const INDUSTRY_OPTIONS = [
  "Banking & Finance", "FinTech", "Insurance", "E-commerce", "Retail", "FMCG",
  "Telecom", "IT & Software", "AI & Data", "Cybersecurity", "Consulting",
  "Real Estate", "Healthcare & MedTech", "Pharma", "Manufacturing",
  "Logistics & Supply Chain", "Energy & Oil & Gas", "Media & Entertainment",
  "EdTech", "Government & Public Sector", "Automotive", "HoReCa", "Legal",
  "HR & Recruitment", "Marketing & Advertising", "Construction", "Agriculture",
  "Travel & Tourism", "Sports & Fitness", "Non-profit & NGO",
];

export function mapToForm(parsed) {
  return {
    First_Name: parsed.first_name ?? "",
    Last_Name: parsed.last_name ?? "",
    Email: parsed.email ?? "",
    Mobile: parsed.mobile ?? "",
    Telegram: parsed.telegram ?? "",
    LinkedIn_s: parsed.linkedin ?? "",
    City: parsed.city ?? "",
    Country: parsed.country ?? "",
    Current_Job_Title: parsed.current_job_title ?? "",
    Current_Employer: parsed.current_employer ?? "",
    Current_Job_Title_Normalized: parsed.normalized_title ?? "",
    seniority_level: parsed.seniority_level ?? "",
    company_scale: parsed.company_scale ?? "",
    career_trajectory: parsed.career_trajectory ?? "",
    pnl_owner: parsed.pnl_owner ?? false,
    board_member: parsed.board_member ?? false,
    team_size: parsed.team_size ?? "",
    total_experience_years: parsed.total_experience_years ?? "",
    industries: parsed.industries ?? [],
    ai_analysis: parsed.ai_analysis ?? "",
    Skill_Set: Array.isArray(parsed.skill_set)
      ? parsed.skill_set.join(", ")
      : parsed.skill_set ?? "",
    Experience_Details: (parsed.experience || []).map((exp) => ({
      Employer: exp.company ?? "",
      Designation: exp.occupation_title ?? "",
      Work_Duration: exp.work_duration ?? "",
      Description: [
        ...(exp.responsibilities || []),
        ...(exp.achievements || []),
      ].join("\n"),
    })),
    Educational_Details: (parsed.education || []).map((edu) => ({
      School: edu.institute_school ?? "",
      Major: edu.major_department ?? "",
      Degree: edu.degree ?? "",
      Duration: edu.duration ?? "",
    })),
  };
}

function Field({ label, name, value, onChange, multiline, options }) {
  const base = {
    width: "100%",
    borderRadius: 6,
    border: "1px solid var(--slate-border)",
    padding: "6px 10px",
    fontSize: 13,
    background: "var(--ink-800)",
    color: "#E8EDF3",
    outline: "none",
    fontFamily: "DM Sans, sans-serif",
    boxSizing: "border-box",
  };
  if (options) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <label style={{ fontSize: 11, color: "var(--slate-muted)", fontWeight: 500 }}>{label}</label>
        <select name={name} value={value ?? ""} onChange={onChange} style={base}>
          {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
      </div>
    );
  }
  if (multiline) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <label style={{ fontSize: 11, color: "var(--slate-muted)", fontWeight: 500 }}>{label}</label>
        <textarea name={name} value={value ?? ""} onChange={onChange}
          rows={3} style={{ ...base, resize: "vertical" }} />
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <label style={{ fontSize: 11, color: "var(--slate-muted)", fontWeight: 500 }}>{label}</label>
      <input name={name} value={value ?? ""} onChange={onChange} style={base} />
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      color: "var(--slate-muted)",
      textTransform: "uppercase",
      marginBottom: 12,
      fontFamily: "JetBrains Mono, monospace",
    }}>
      {children}
    </div>
  );
}

function ExperienceBlock({ items, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {(items || []).map((exp, i) => (
        <div key={i} style={{
          borderRadius: 8,
          border: "1px solid var(--slate-border)",
          padding: 12,
          background: "var(--ink-900)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--acid)", fontFamily: "JetBrains Mono, monospace" }}>
            #{i + 1}
          </div>
          {[
            ["Компания", "Employer"],
            ["Должность", "Designation"],
            ["Период", "Work_Duration"],
            ["Обязанности и достижения", "Description"],
          ].map(([label, field]) => (
            <Field key={field} label={label} name={field} value={exp[field] ?? ""}
              multiline={field === "Description"}
              onChange={(e) => {
                const updated = items.map((x, idx) =>
                  idx === i ? { ...x, [field]: e.target.value } : x
                );
                onChange(updated);
              }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function EducationBlock({ items, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {(items || []).map((edu, i) => (
        <div key={i} style={{
          borderRadius: 8,
          border: "1px solid var(--slate-border)",
          padding: 12,
          background: "var(--ink-900)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--acid)", fontFamily: "JetBrains Mono, monospace" }}>
            #{i + 1}
          </div>
          {[
            ["Учебное заведение", "School"],
            ["Специальность", "Major"],
            ["Степень", "Degree"],
            ["Период", "Duration"],
          ].map(([label, field]) => (
            <Field key={field} label={label} name={field} value={edu[field] ?? ""}
              onChange={(e) => {
                const updated = items.map((x, idx) =>
                  idx === i ? { ...x, [field]: e.target.value } : x
                );
                onChange(updated);
              }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function CandidatePreview({
  data,
  filename,
  onConfirm,
  onCancel,
  loading,
  confirmLabel = "✓ Отправить в Zoho Recruit",
  showHeader = true,
  initialForm,
}) {
  const [form, setForm] = useState(() => initialForm ?? mapToForm(data));

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };
  const handleBool = (name) => {
    setForm((f) => ({ ...f, [name]: !f[name] }));
  };

  const section = {
    paddingBottom: 24,
    borderBottom: "1px solid var(--slate-border)",
  };

  return (
    <div style={{
      marginTop: showHeader ? 24 : 0,
      borderRadius: showHeader ? 12 : 0,
      border: showHeader ? "1px solid var(--slate-border)" : "none",
      background: "var(--ink-800)",
      overflow: "hidden",
    }}>
      {/* Header */}
      {showHeader && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          background: "var(--ink-900)",
          borderBottom: "1px solid var(--slate-border)",
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#E8EDF3" }}>
              Предпросмотр карточки
            </div>
            <div style={{ fontSize: 11, color: "var(--slate-muted)", marginTop: 2 }}>
              {filename}
            </div>
          </div>
          <button onClick={onCancel} style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 12, color: "var(--slate-muted)",
            padding: "4px 8px",
          }}>
            ✕ Отмена
          </button>
        </div>
      )}

      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 24 }}>

        {/* Контакты */}
        <section style={section}>
          <SectionTitle>Контакты</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Имя" name="First_Name" value={form.First_Name} onChange={handleChange} />
            <Field label="Фамилия" name="Last_Name" value={form.Last_Name} onChange={handleChange} />
            <Field label="Email" name="Email" value={form.Email} onChange={handleChange} />
            <Field label="Телефон" name="Mobile" value={form.Mobile} onChange={handleChange} />
            <Field label="Telegram" name="Telegram" value={form.Telegram} onChange={handleChange} />
            <Field label="LinkedIn" name="LinkedIn_s" value={form.LinkedIn_s} onChange={handleChange} />
            <Field label="Город" name="City" value={form.City} onChange={handleChange} />
            <Field label="Страна" name="Country" value={form.Country} onChange={handleChange} />
          </div>
        </section>

        {/* Текущая позиция */}
        <section style={section}>
          <SectionTitle>Текущая позиция</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Должность" name="Current_Job_Title" value={form.Current_Job_Title} onChange={handleChange} />
            <Field label="Работодатель" name="Current_Employer" value={form.Current_Employer} onChange={handleChange} />
            <Field label="Нормализованная должность" name="Current_Job_Title_Normalized" value={form.Current_Job_Title_Normalized} onChange={handleChange} />
          </div>
        </section>

        {/* AI-оценка */}
        <section style={section}>
          <SectionTitle>AI-оценка</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Field label="Уровень" name="seniority_level" value={form.seniority_level}
              onChange={handleChange} options={SENIORITY_OPTIONS} />
            <Field label="Масштаб компании" name="company_scale" value={form.company_scale}
              onChange={handleChange} options={SCALE_OPTIONS} />
            <Field label="Карьерная траектория" name="career_trajectory" value={form.career_trajectory}
              onChange={handleChange} options={TRAJECTORY_OPTIONS} />
          </div>
          <div style={{ display: "flex", gap: 20, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
            {[["Управлял P&L", "pnl_owner"], ["Член совета директоров", "board_member"]].map(([label, key]) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form[key]} onChange={() => handleBool(key)}
                  style={{ width: 14, height: 14, accentColor: "var(--acid)" }} />
                <span style={{ fontSize: 13, color: "var(--slate-text)" }}>{label}</span>
              </label>
            ))}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, color: "var(--slate-muted)" }}>Размер команды:</span>
              <input type="number" name="team_size" value={form.team_size ?? ""}
                onChange={handleChange}
                style={{
                  width: 72, borderRadius: 6, border: "1px solid var(--slate-border)",
                  padding: "4px 8px", fontSize: 13, background: "var(--ink-800)",
                  color: "#E8EDF3", outline: "none",
                }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, color: "var(--slate-muted)" }}>Лет опыта:</span>
              <input type="number" name="total_experience_years" value={form.total_experience_years ?? ""}
                onChange={handleChange}
                style={{
                  width: 72, borderRadius: 6, border: "1px solid var(--slate-border)",
                  padding: "4px 8px", fontSize: 13, background: "var(--ink-800)",
                  color: "#E8EDF3", outline: "none",
                }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "var(--slate-muted)", fontWeight: 500, marginBottom: 8 }}>Отрасли</div>
            <div style={{
              display: "flex", flexWrap: "wrap", gap: "6px 16px",
              padding: "10px 12px",
              borderRadius: 6, border: "1px solid var(--slate-border)",
              background: "var(--ink-900)",
            }}>
              {INDUSTRY_OPTIONS.map((ind) => (
                <label key={ind} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={(form.industries ?? []).includes(ind)}
                    onChange={() => {
                      setForm((f) => {
                        const current = f.industries ?? [];
                        const next = current.includes(ind)
                          ? current.filter((x) => x !== ind)
                          : [...current, ind];
                        return { ...f, industries: next };
                      });
                    }}
                    style={{ width: 13, height: 13, accentColor: "var(--acid)" }}
                  />
                  <span style={{ fontSize: 12, color: "var(--slate-text)" }}>{ind}</span>
                </label>
              ))}
            </div>
          </div>
          <Field label="AI-саммари" name="ai_analysis" value={form.ai_analysis}
            onChange={handleChange} multiline />
        </section>

        {/* Навыки */}
        <section style={section}>
          <SectionTitle>Навыки</SectionTitle>
          <Field label="" name="Skill_Set" value={form.Skill_Set} onChange={handleChange} multiline />
        </section>

        {/* Опыт */}
        <section style={section}>
          <SectionTitle>Опыт работы</SectionTitle>
          <ExperienceBlock
            items={form.Experience_Details}
            onChange={(updated) => setForm((f) => ({ ...f, Experience_Details: updated }))}
          />
        </section>

        {/* Образование */}
        <section style={{ paddingBottom: 8 }}>
          <SectionTitle>Образование</SectionTitle>
          <EducationBlock
            items={form.Educational_Details}
            onChange={(updated) => setForm((f) => ({ ...f, Educational_Details: updated }))}
          />
        </section>

        {/* Кнопки */}
        <div style={{ display: "flex", gap: 12, paddingTop: 8 }}>
          <button onClick={() => onConfirm(form)} disabled={loading}
            style={{
              flex: 1, padding: "11px 20px",
              background: loading ? "var(--ink-700)" : "var(--acid)",
              border: "none", borderRadius: 8,
              fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14,
              color: "var(--ink-950)", cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.15s ease",
            }}>
            {loading ? "Отправка…" : confirmLabel}
          </button>
          <button onClick={onCancel} disabled={loading}
            style={{
              padding: "11px 20px",
              background: "none",
              border: "1px solid var(--slate-border)",
              borderRadius: 8, fontSize: 13,
              color: "var(--slate-text)", cursor: loading ? "not-allowed" : "pointer",
            }}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
